import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiClock,
  FiMapPin,
  FiRefreshCw,
  FiSearch,
  FiBriefcase,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';
import '../styles/GpsTracking.css';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';

// Fix missing default marker icons in some bundlers.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const USERS_QUERY = `
  query GpsUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id firstName lastName email roles employeeCode branchId contact address
        jobTitle typeOfWork upload
      }
      totalPages
    }
  }
`;

const TIME_CARDS_QUERY = `
  query GpsTimeCards($date: String) {
    findTimeCards(date: $date) {
      id
      userId
      date
      timeIn
      timeOut
      duration
      notes
      latitude
      longitude
      user { id firstName lastName employeeCode }
    }
  }
`;

const BRANCHES_QUERY = `
  query GpsBranches {
    getMyCompanyBranches {
      id name location postcode latitude longitude
    }
  }
`;

const PROJECTS_QUERY = `
  query GpsProjects {
    findAllProject {
      id
      projectId
      name
      status
      startDate
      deadLine
      startTime
      endTime
      booking { id address fullName phone }
      manager { id firstName lastName }
      tasks {
        id
        title
        location
        date
        time
        status
        user { id }
        team {
          id
          members { id }
        }
      }
    }
  }
`;

const TASKS_BY_USER_QUERY = `
  query GetTasksByUser($userId: String!) {
    getTasksByUser(userId: $userId) {
      id
      title
      status
      date
      time
      bookingId
      project {
        id
        projectId
        name
        startDate
        deadLine
        startTime
        endTime
        status
        booking {
          id
          fullName
          address
        }
      }
      user { id }
      team { id }
    }
  }
`;

const HISTORY_QUERY = `
  query GetGpsTrackHistory(
    $userId: String
    $from: String
    $to: String
    $limit: Int
  ) {
    getGpsTrackHistory(
      userId: $userId
      from: $from
      to: $to
      limit: $limit
    ) {
      id
      userId
      timeCardId
      projectId
      projectName
      latitude
      longitude
      accuracy
      source
      recordedAt
      notes
    }
  }
`;

const DEFAULT_BOUNDS = {
  minLat: -34.05,
  maxLat: -33.7,
  minLng: 150.85,
  maxLng: 151.35,
};

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function formatClock(value) {
  if (value == null || value === '') return null;
  const match = String(value).trim().match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return `${String(match[1]).padStart(2, '0')}:${match[2]}`;
}

function isOpenClock(card) {
  if (!card?.timeIn) return false;
  const notes = String(card.notes || '');
  if (notes.includes('[CLOCK_OPEN]')) return true;
  try {
    const parsed = JSON.parse(notes);
    if (String(parsed?.type || '').toLowerCase().includes('open')) return true;
    if (String(parsed?.type || '').toLowerCase().includes('report')) return false;
  } catch {
    /* ignore */
  }
  const inn = formatClock(card.timeIn);
  const out = formatClock(card.timeOut);
  return Boolean(inn && out && inn === out);
}

function parseNotes(notes) {
  const raw = String(notes || '');
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return {
        projectId: parsed.projectId || '',
        projectName: parsed.projectName || parsed.projectCode || '',
        location: parsed.location || parsed.address || '',
      };
    }
  } catch {
    /* fall through */
  }
  const projectMatch = raw.match(/\[PROJECT:([^\]]+)\]/i);
  const nameMatch = raw.match(/\[PROJECT_NAME:([^\]]+)\]/i);
  const locMatch = raw.match(/\[LOCATION:([^\]]+)\]/i);
  return {
    projectId: projectMatch?.[1]?.trim() || '',
    projectName: nameMatch?.[1]?.trim() || '',
    location: locMatch?.[1]?.trim() || '',
  };
}

function initials(name) {
  return (
    String(name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || '')
      .join('') || '?'
  );
}

function avatarColor(name) {
  const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4', '#ef4444'];
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i) * 17) % colors.length;
  return colors[h];
}

function onSiteDuration(card) {
  if (!card?.timeIn) return '—';
  const [hh, mm] = String(formatClock(card.timeIn) || '0:0').split(':').map(Number);
  const start = new Date();
  start.setHours(hh || 0, mm || 0, 0, 0);
  let diff = Math.max(0, Date.now() - start.getTime());
  if (!isOpenClock(card) && card.duration) return card.duration;
  const mins = Math.floor(diff / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function haversineKm(lat1, lng1, lat2, lng2) {
  if (![lat1, lng1, lat2, lng2].every((n) => Number.isFinite(Number(n)))) {
    return null;
  }
  const toRad = (d) => (Number(d) * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 10) / 10;
}

function toMapPercent(lat, lng, bounds) {
  const y =
    ((bounds.maxLat - Number(lat)) / (bounds.maxLat - bounds.minLat)) * 100;
  const x =
    ((Number(lng) - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * 100;
  return {
    left: `${Math.min(96, Math.max(4, x))}%`,
    top: `${Math.min(94, Math.max(6, y))}%`,
  };
}

function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function sourceLabel(source) {
  const s = String(source || '').toLowerCase();
  if (s === 'clock_in') return 'Clock in';
  if (s === 'clock_out') return 'Clock out';
  if (s === 'job_switch') return 'Job switch';
  if (s === 'import') return 'Imported';
  return 'Live ping';
}

function buildJobsFromClockNotes(projects, clockNotes) {
  const jobs = [];
  if (!(clockNotes?.projectId || clockNotes?.projectName)) return jobs;

  const match = projects.find((p) => p.id === clockNotes.projectId);
  const key = clockNotes.projectId || `notes:${clockNotes.projectName}`;
  jobs.push({
    id: clockNotes.projectId || key,
    projectUuid: match?.id || clockNotes.projectId || '',
    code: match?.projectId || '',
    name: clockNotes.projectName || match?.name || 'Current shift job',
    client: match?.booking?.fullName || '—',
    address: clockNotes.location || match?.booking?.address || '—',
    phone: match?.booking?.phone || '',
    startDate: match?.startDate,
    deadLine: match?.deadLine,
    startTime: match?.startTime,
    endTime: match?.endTime,
    status: match?.status || 'active',
    taskTitle: '',
    fromClock: true,
  });

  return jobs;
}

function buildJobsFromTasks(tasks, { markFromClockProjectId } = {}) {
  const jobsByProjectId = new Map();

  (tasks || []).forEach((t) => {
    const p = t?.project;
    if (!p?.id) return;
    const pid = p.id;

    if (!jobsByProjectId.has(pid)) {
      jobsByProjectId.set(pid, {
        id: pid,
        projectUuid: pid,
        code: p.projectId || '',
        name: p.name || 'Untitled project',
        client: p.booking?.fullName || '—',
        address: p.booking?.address || t.location || '—',
        phone: p.booking?.phone || '',
        startDate: p.startDate,
        deadLine: p.deadLine,
        startTime: p.startTime,
        endTime: p.endTime,
        status: p.status || 'active',
        taskTitleList: [],
        taskTitle: '',
        fromClock: false,
      });
    }

    const job = jobsByProjectId.get(pid);
    const title = String(t?.title || '').trim();
    if (title) job.taskTitleList.push(title);
  });

  const jobs = Array.from(jobsByProjectId.values()).map((job) => {
    job.taskTitle = job.taskTitleList.join(', ');
    delete job.taskTitleList;
    if (
      markFromClockProjectId &&
      String(job.projectUuid) === String(markFromClockProjectId)
    ) {
      job.fromClock = true;
    }
    return job;
  });

  jobs.sort((a, b) => Number(Boolean(b.fromClock)) - Number(Boolean(a.fromClock)));
  return jobs;
}

export default function GpsTracking() {
  const { get, patch } = useAdminPageSearch();
  const urlTab = get('tab');
  const tab = urlTab === 'history' ? 'history' : 'live';
  const urlMember = get('member');
  const urlJob = get('job');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState(urlMember || '');
  const [selectedJobId, setSelectedJobId] = useState(urlJob || '');
  const [search, setSearch] = useState('');
  const [regionLabel, setRegionLabel] = useState('Live Map');

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPoints, setHistoryPoints] = useState([]);
  const [historyFrom, setHistoryFrom] = useState(daysAgoISO(7));
  const [historyTo, setHistoryTo] = useState(todayISO());
  const [historyUserId, setHistoryUserId] = useState('');
  const [historySelectedId, setHistorySelectedId] = useState('');

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const date = todayISO();
      const [usersAll, cardsData, branchesData, projectsData] = await Promise.all([
        (async () => {
          const all = [];
          let page = 1;
          let totalPages = 1;
          do {
            let data;
            try {
              data = await graphqlRequest(USERS_QUERY, { page, limit: 100 });
            } catch {
              data = await graphqlRequest(
                USERS_QUERY.replace(/\s*upload\s*/, '\n'),
                { page, limit: 100 },
              );
            }
            const result = data?.getAllUser;
            all.push(...(result?.data || []));
            totalPages = result?.totalPages || 1;
            page += 1;
          } while (page <= totalPages && page <= 20);
          return all;
        })(),
        graphqlRequest(TIME_CARDS_QUERY, { date }).catch(async (err) => {
          if (String(err?.message || '').toLowerCase().includes('latitude')) {
            return graphqlRequest(
              `
              query GpsTimeCards($date: String) {
                findTimeCards(date: $date) {
                  id userId date timeIn timeOut duration notes
                  user { id firstName lastName employeeCode }
                }
              }
            `,
              { date },
            );
          }
          throw err;
        }),
        graphqlRequest(BRANCHES_QUERY).catch(() => ({ getMyCompanyBranches: [] })),
        graphqlRequest(PROJECTS_QUERY).catch(() => ({ findAllProject: [] })),
      ]);

      const branches = branchesData?.getMyCompanyBranches || [];
      const branchById = new Map(branches.map((b) => [b.id, b]));
      const defaultBranch =
        branches.find((b) => b.latitude != null && b.longitude != null) ||
        branches[0];

      setRegionLabel(
        defaultBranch?.name ? `Live Map — ${defaultBranch.name}` : 'Live Map',
      );

      const cards = cardsData?.findTimeCards || [];
      const openByUser = new Map();
      cards.forEach((c) => {
        if (!c?.userId || !isOpenClock(c)) return;
        openByUser.set(c.userId, c);
      });

      const projects = projectsData?.findAllProject || [];

      const mapped = (usersAll || []).map((u, index) => {
        const name =
          `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
        const card = openByUser.get(u.id);
        const online = Boolean(card);
        const notes = parseNotes(card?.notes);
        const branch = branchById.get(u.branchId) || defaultBranch;
        // Start with only the active-shift job (from time-card notes).
        // When user selects someone, we fetch full tasks via getTasksByUser.
        const jobs = buildJobsFromClockNotes(projects, notes);
        const jobsLoaded = false;

        let lat = card?.latitude != null ? Number(card.latitude) : null;
        let lng = card?.longitude != null ? Number(card.longitude) : null;
        let hasGpsFix =
          Number.isFinite(lat) &&
          Number.isFinite(lng) &&
          !(lat === 0 && lng === 0);

        if (!hasGpsFix && online) {
          lat = branch?.latitude != null ? Number(branch.latitude) : null;
          lng = branch?.longitude != null ? Number(branch.longitude) : null;
          if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
            const baseLat = DEFAULT_BOUNDS.minLat + 0.15;
            const baseLng = DEFAULT_BOUNDS.minLng + 0.2;
            lat = baseLat + ((index % 7) - 3) * 0.02;
            lng = baseLng + ((index % 5) - 2) * 0.03;
          }
          hasGpsFix = false;
        } else if (!hasGpsFix) {
          lat = null;
          lng = null;
        }

        const homeLat = branch?.latitude != null ? Number(branch.latitude) : null;
        const homeLng = branch?.longitude != null ? Number(branch.longitude) : null;
        const distanceKm = haversineKm(homeLat, homeLng, lat, lng);
        const primaryJob = jobs[0] || null;

        return {
          id: u.id,
          name,
          initials: initials(name),
          avatar: avatarColor(name),
          online,
          jobs,
          jobsLoaded,
          jobName: primaryJob?.name || (online ? 'On shift' : 'Not clocked in'),
          location: primaryJob?.address || u.address || branch?.location || '—',
          route: branch?.name || 'Route',
          onSite: online ? onSiteDuration(card) : '—',
          distance:
            distanceKm != null ? `${distanceKm} km` : '—',
          lat,
          lng,
          approximateLocation: online && !hasGpsFix,
          timeIn: card?.timeIn || null,
          contact: u.contact || '—',
        };
      });

      mapped.sort(
        (a, b) =>
          Number(b.online) - Number(a.online) ||
          a.name.localeCompare(b.name),
      );
      setEmployees(mapped);
      setSelectedId((prev) => {
        if (prev && mapped.some((e) => e.id === prev)) return prev;
        const fromUrl =
          new URLSearchParams(window.location.search).get('member') || '';
        if (fromUrl) return fromUrl;
        return mapped.find((e) => e.online)?.id || mapped[0]?.id || '';
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to load GPS tracking');
      setEmployees([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const ensureJobsLoadedForUser = useCallback(
    async (userId) => {
      if (!userId) return;
      const emp = employees.find((e) => e.id === userId);
      if (!emp || emp.jobsLoaded) return;

      // Preserve currently-open job selection.
      const markFromClockProjectId =
        emp.jobs?.find((j) => j.fromClock)?.projectUuid || null;

      try {
        const data = await graphqlRequest(TASKS_BY_USER_QUERY, { userId });
        const tasks = Array.isArray(data?.getTasksByUser)
          ? data.getTasksByUser
          : [];
        const jobs = buildJobsFromTasks(tasks, {
          markFromClockProjectId,
        });

        setEmployees((prev) =>
          prev.map((x) => {
            if (x.id !== userId) return x;
            const primaryJob = jobs[0] || x.jobs?.[0] || null;
            return {
              ...x,
              jobs,
              jobsLoaded: true,
              jobName: primaryJob?.name || x.jobName,
              location: primaryJob?.address || x.location,
            };
          }),
        );
      } catch (err) {
        console.error('Failed loading GPS job details:', err);
      }
    },
    [employees],
  );

  useEffect(() => {
    if (!selectedId) return;
    void ensureJobsLoadedForUser(selectedId);
  }, [selectedId, ensureJobsLoadedForUser]);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const data = await graphqlRequest(HISTORY_QUERY, {
        userId: historyUserId || null,
        from: historyFrom || null,
        to: historyTo || null,
        limit: 400,
      });
      const rows = Array.isArray(data?.getGpsTrackHistory)
        ? data.getGpsTrackHistory
        : [];
      setHistoryPoints(rows);
      setHistorySelectedId((prev) => {
        if (prev && rows.some((r) => r.id === prev)) return prev;
        return rows[0]?.id || '';
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to load GPS history');
      setHistoryPoints([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyUserId, historyFrom, historyTo]);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(true), 45000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (urlMember) setSelectedId(urlMember);
  }, [urlMember]);

  useEffect(() => {
    if (tab === 'history') void loadHistory();
  }, [tab, loadHistory]);

  useEffect(() => {
    const emp = employees.find((e) => e.id === selectedId);
    if (!emp) {
      setSelectedJobId('');
      return;
    }
    setSelectedJobId((prev) => {
      if (urlJob && emp.jobs.some((j) => j.id === urlJob)) return urlJob;
      if (prev && emp.jobs.some((j) => j.id === prev)) return prev;
      return emp.jobs[0]?.id || '';
    });
  }, [employees, selectedId]);

  const onlineCount = useMemo(
    () => employees.filter((e) => e.online).length,
    [employees],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.jobName.toLowerCase().includes(q) ||
        e.location.toLowerCase().includes(q) ||
        e.jobs.some(
          (j) =>
            j.name.toLowerCase().includes(q) ||
            j.client.toLowerCase().includes(q),
        ),
    );
  }, [employees, search]);

  const selectMember = (id, jobId) => {
    setSelectedId(id);
    if (jobId) {
      setSelectedJobId(jobId);
      patch({ member: id, job: jobId });
    } else {
      patch({ member: id }, ['job']);
    }
  };

  const selectJob = (jobId) => {
    setSelectedJobId(jobId);
    patch({ job: jobId });
  };

  const selected = useMemo(
    () => employees.find((e) => e.id === selectedId) || null,
    [employees, selectedId],
  );

  const selectedJob = useMemo(() => {
    if (!selected) return null;
    return (
      selected.jobs.find((j) => j.id === selectedJobId) ||
      selected.jobs[0] ||
      null
    );
  }, [selected, selectedJobId]);

  // Auto-select the currently-active job for this employee (the one coming from clock-in).
  useEffect(() => {
    if (!selected) return;
    const active = selected.jobs.find((j) => j.fromClock);
    const nextId = active?.id || selected.jobs?.[0]?.id || '';
    setSelectedJobId((prev) => {
      if (urlJob && selected.jobs.some((j) => j.id === urlJob)) return urlJob;
      if (prev && selected.jobs.some((j) => j.id === prev)) return prev;
      return nextId;
    });
  }, [selectedId, selected]);

  const mapEmployees = useMemo(
    () => employees.filter((e) => e.online && e.lat != null && e.lng != null),
    [employees],
  );

  const historyNameById = useMemo(() => {
    const map = new Map(employees.map((e) => [e.id, e.name]));
    return map;
  }, [employees]);

  const bounds = useMemo(() => {
    const points =
      tab === 'history'
        ? historyPoints.map((p) => ({ lat: p.latitude, lng: p.longitude }))
        : mapEmployees;
    if (!points.length) return DEFAULT_BOUNDS;
    const lats = points.map((e) => Number(e.lat ?? e.latitude));
    const lngs = points.map((e) => Number(e.lng ?? e.longitude));
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const padLat = Math.max(0.05, (maxLat - minLat) * 0.25 || 0.08);
    const padLng = Math.max(0.05, (maxLng - minLng) * 0.25 || 0.08);
    return {
      minLat: minLat - padLat,
      maxLat: maxLat + padLat,
      minLng: minLng - padLng,
      maxLng: maxLng + padLng,
    };
  }, [tab, mapEmployees, historyPoints]);

  const leafletBounds = useMemo(() => {
    return [
      [bounds.minLat, bounds.minLng],
      [bounds.maxLat, bounds.maxLng],
    ];
  }, [bounds]);

  const historyTrail = useMemo(() => {
    const rows = [...historyPoints].reverse();
    return rows
      .filter((p) => Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)))
      .map((p) => ({
        ...p,
        pos: toMapPercent(p.latitude, p.longitude, bounds),
      }));
  }, [historyPoints, bounds]);

  return (
    <div className="gps-page">
      <header className="gps-header">
        <div>
          <h1>GPS Tracking</h1>
          <p>
            Live locations, multi-job assignment details, and full movement
            history for every employee.
          </p>
        </div>
        <div className="gps-header-actions">
          <span className="gps-online-pill">{onlineCount} Online</span>
          <button
            type="button"
            className="gps-refresh"
            onClick={() =>
              tab === 'live' ? void load(true) : void loadHistory()
            }
            disabled={refreshing || historyLoading}
          >
            <FiRefreshCw
              className={refreshing || historyLoading ? 'is-spin' : ''}
            />{' '}
            Refresh
          </button>
        </div>
      </header>

      <div className="gps-tabs">
        <button
          type="button"
          className={tab === 'live' ? 'is-active' : ''}
          onClick={() => patch({ tab: '' })}
        >
          Live
        </button>
        <button
          type="button"
          className={tab === 'history' ? 'is-active' : ''}
          onClick={() => patch({ tab: 'history' })}
        >
          History
        </button>
      </div>

      {tab === 'live' ? (
        <>
          <section className="gps-map-card">
            <div className="gps-map-toolbar">
              <h2>{regionLabel}</h2>
            </div>

            <div className={`gps-map gps-map--terrain`}>
              <div className="gps-map-legend">
                <span>
                  <i className="gps-dot gps-dot--on" /> Online
                </span>
                <span>
                  <i className="gps-dot gps-dot--off" /> Offline
                </span>
              </div>

              {loading ? (
                <div className="gps-map-empty">Loading map…</div>
              ) : mapEmployees.length === 0 ? (
                <div className="gps-map-empty">
                  No one is clocked in with a location yet. Online employees
                  appear here after clock-in.
                </div>
              ) : (
                <MapContainer
                  bounds={leafletBounds}
                  scrollWheelZoom={false}
                  style={{ height: 420, width: '100%' }}
                >
                  <TileLayer
                    // Use Carto tiles to avoid label language issues seen on raw OSM tiles.
                    // Voyager generally renders in English.
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                    attribution="&copy; CartoDB &copy; OpenStreetMap contributors"
                  />

                  {mapEmployees.map((emp) => {
                    const activeJob = emp.jobs.find((j) => j.fromClock) || null;
                    const isSelected = emp.id === selectedId;
                    const onlineOpacity = emp.online ? 1 : 0.45;
                    const icon = L.divIcon({
                      className: 'gps-live-divicon',
                      html: `
                        <div
                          style="
                            width:38px;height:38px;border-radius:50%;
                            background:${emp.avatar};
                            border:3px solid #fff;
                            color:#fff;
                            display:flex;align-items:center;justify-content:center;
                            font-weight:800;font-size:12px;
                            box-shadow: 0 4px 12px rgba(15,23,42,0.25);
                            opacity:${onlineOpacity};
                            ${isSelected ? 'transform: scale(1.08); box-shadow: 0 0 0 3px rgba(34,197,94,0.35), 0 4px 12px rgba(15,23,42,0.25);' : ''}
                            cursor:pointer;
                          "
                        >
                          ${emp.initials}
                        </div>
                      `,
                      iconSize: [38, 38],
                      iconAnchor: [19, 19],
                    });
                    return (
                      <Marker
                        key={emp.id}
                        position={[emp.lat, emp.lng]}
                        icon={icon}
                        eventHandlers={{
                          click: () => selectMember(emp.id),
                        }}
                      >
                        <Popup>
                          <div style={{ minWidth: 160 }}>
                            <strong>{emp.name}</strong>
                            <div
                              style={{
                                color: '#64748b',
                                fontSize: 12,
                                marginTop: 4,
                              }}
                            >
                              {activeJob
                                ? `${activeJob.name} — ${activeJob.client}`
                                : emp.jobName}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              )}

              {selected ? (
                <aside className="gps-side-panel">
                  <div className="gps-side-head">
                    <div
                      className="gps-side-avatar"
                      style={{ background: selected.avatar }}
                    >
                      {selected.initials}
                    </div>
                    <div>
                      <strong>{selected.name}</strong>
                      <span className={selected.online ? 'is-on' : 'is-off'}>
                        {selected.online ? 'Online' : 'Offline'}
                      </span>
                    </div>
                  </div>

                  <div className="gps-job-select">
                    <label htmlFor="gps-job">
                      <FiBriefcase /> Assigned jobs ({selected.jobs.length})
                    </label>
                    {selected.jobs.length === 0 ? (
                      <p className="gps-muted">No assigned jobs found.</p>
                    ) : (
                      <select
                        id="gps-job"
                        value={selectedJobId}
                        onChange={(e) => selectJob(e.target.value)}
                      >
                        {selected.jobs.map((job) => (
                          <option key={job.id} value={job.id}>
                            {job.name}
                            {job.fromClock ? ' (active shift)' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {selectedJob ? (
                    <dl className="gps-side-dl">
                      <div>
                        <dt>Job</dt>
                        <dd>{selectedJob.name}</dd>
                      </div>
                      {selectedJob.code ? (
                        <div>
                          <dt>Project ID</dt>
                          <dd>{selectedJob.code}</dd>
                        </div>
                      ) : null}
                      <div>
                        <dt>Client</dt>
                        <dd>{selectedJob.client}</dd>
                      </div>
                      <div>
                        <dt>Location</dt>
                        <dd>{selectedJob.address}</dd>
                      </div>
                      {selectedJob.taskTitle ? (
                        <div>
                          <dt>Tasks</dt>
                          <dd>{selectedJob.taskTitle}</dd>
                        </div>
                      ) : null}
                      <div>
                        <dt>Schedule</dt>
                        <dd>
                          {[selectedJob.startTime, selectedJob.endTime]
                            .filter(Boolean)
                            .join(' – ') || '—'}
                        </dd>
                      </div>
                      <div>
                        <dt>Time on Site</dt>
                        <dd>{selected.onSite}</dd>
                      </div>
                      <div>
                        <dt>Distance</dt>
                        <dd>
                          {selected.distance}
                          {selected.approximateLocation ? ' · Approx.' : ''}
                        </dd>
                      </div>
                    </dl>
                  ) : (
                    <dl className="gps-side-dl">
                      <div>
                        <dt>Status</dt>
                        <dd>{selected.jobName}</dd>
                      </div>
                      <div>
                        <dt>Location</dt>
                        <dd>{selected.location}</dd>
                      </div>
                    </dl>
                  )}
                </aside>
              ) : null}
            </div>
          </section>

          <section className="gps-list-section">
            <div className="gps-list-head">
              <h2>Employees</h2>
              <div className="gps-search">
                <FiSearch />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search employee or job..."
                />
              </div>
            </div>

            {loading ? (
              <div className="gps-empty">Loading employees…</div>
            ) : filtered.length === 0 ? (
              <div className="gps-empty">No employees match your search.</div>
            ) : (
              <div className="gps-grid">
                {filtered.map((emp) => (
                  <article
                    key={emp.id}
                    className={`gps-card${emp.id === selectedId ? ' is-selected' : ''}`}
                    onClick={() => selectMember(emp.id)}
                  >
                    <div className="gps-card-top">
                      <div className="gps-card-id">
                        <div
                          className="gps-side-avatar sm"
                          style={{ background: emp.avatar }}
                        >
                          {emp.initials}
                        </div>
                        <div>
                          <strong>{emp.name}</strong>
                          <span className={emp.online ? 'is-on' : 'is-off'}>
                            {emp.online ? 'Online' : 'Offline'}
                          </span>
                        </div>
                      </div>
                      <FiMapPin className={emp.online ? 'is-on' : ''} />
                    </div>
                    <div className="gps-card-job">
                      {emp.jobs.length > 1
                        ? `${emp.jobs.length} jobs · ${emp.jobName}`
                        : emp.jobName}
                    </div>
                    <div className="gps-card-loc">{emp.location}</div>
                    {emp.jobs.length > 1 ? (
                      <div className="gps-job-chips">
                        {emp.jobs.slice(0, 3).map((j) => (
                          <button
                            key={j.id}
                            type="button"
                            className={
                              emp.id === selectedId && j.id === selectedJobId
                                ? 'is-active'
                                : ''
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              selectMember(emp.id, j.id);
                            }}
                          >
                            {j.name}
                          </button>
                        ))}
                        {emp.jobs.length > 3 ? (
                          <span>+{emp.jobs.length - 3}</span>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="gps-card-stats">
                      <div>
                        <span>Route</span>
                        <strong>{emp.route}</strong>
                      </div>
                      <div>
                        <span>On Site</span>
                        <strong>{emp.onSite}</strong>
                      </div>
                      <div>
                        <span>Distance</span>
                        <strong>{emp.distance}</strong>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      ) : (
        <section className="gps-history">
          <div className="gps-history-filters">
            <label>
              Employee
              <select
                value={historyUserId}
                onChange={(e) => setHistoryUserId(e.target.value)}
              >
                <option value="">All employees</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              From
              <input
                type="date"
                value={historyFrom}
                onChange={(e) => setHistoryFrom(e.target.value)}
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={historyTo}
                onChange={(e) => setHistoryTo(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="gps-refresh"
              onClick={() => void loadHistory()}
              disabled={historyLoading}
            >
              <FiClock /> {historyLoading ? 'Loading…' : 'Load history'}
            </button>
          </div>

          <div className="gps-history-layout">
            <div className="gps-map gps-map--terrain gps-map--history">
              {historyLoading ? (
                <div className="gps-map-empty">Loading trail…</div>
              ) : historyPoints.length === 0 || historyTrail.length === 0 ? (
                <div className="gps-map-empty">
                  No GPS history in this range yet. Points appear when staff
                  clock in/out or send location updates.
                </div>
              ) : (
                <MapContainer
                  bounds={leafletBounds}
                  scrollWheelZoom={false}
                  style={{ height: 420, width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                    attribution="&copy; CartoDB &copy; OpenStreetMap contributors"
                  />

                  <Polyline
                    positions={historyPoints
                      .slice()
                      .sort(
                        (a, b) =>
                          new Date(a.recordedAt).getTime() -
                          new Date(b.recordedAt).getTime(),
                      )
                      .map((p) => [p.latitude, p.longitude])}
                    pathOptions={{ color: '#3b82f6', weight: 2, opacity: 0.85 }}
                  />

                  {historyTrail.map((p, idx) => {
                    const isLast = idx === historyTrail.length - 1;
                    const isActive = p.id === historySelectedId;
                    const bg = isLast ? '#16a34a' : '#3b82f6';
                    const color = isActive ? '#111827' : '#ffffff';
                    const icon = L.divIcon({
                      className: 'gps-history-divicon',
                      html: `<div style="
                        width:28px;height:28px;border-radius:50%;
                        background:${bg};
                        border:3px solid #fff;
                        display:flex;align-items:center;justify-content:center;
                        color:${color};font-weight:800;font-size:12px;
                        box-shadow: 0 4px 12px rgba(15,23,42,0.25);
                      ">${idx + 1}</div>`,
                      iconSize: [28, 28],
                      iconAnchor: [14, 14],
                    });
                    return (
                      <Marker
                        key={p.id}
                        position={[p.latitude, p.longitude]}
                        icon={icon}
                        eventHandlers={{
                          click: () => setHistorySelectedId(p.id),
                        }}
                      >
                        <Popup>
                          <strong>{formatWhen(p.recordedAt)}</strong>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                            {p.projectName || 'No job linked'}
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              )}
            </div>

            <div className="gps-history-list">
              <h3>
                Track points{' '}
                <span>({historyPoints.length})</span>
              </h3>
              {historyLoading ? (
                <div className="gps-empty">Loading…</div>
              ) : historyPoints.length === 0 ? (
                <div className="gps-empty">No history found.</div>
              ) : (
                <ul>
                  {historyPoints.map((p) => (
                    <li
                      key={p.id}
                      className={p.id === historySelectedId ? 'is-active' : ''}
                      onClick={() => setHistorySelectedId(p.id)}
                    >
                      <div>
                        <strong>
                          {historyNameById.get(p.userId) || 'Employee'}
                        </strong>
                        <span>{formatWhen(p.recordedAt)}</span>
                      </div>
                      <div className="gps-history-meta">
                        <em>{sourceLabel(p.source)}</em>
                        <span>
                          {p.projectName || 'No job linked'} ·{' '}
                          {Number(p.latitude).toFixed(5)},{' '}
                          {Number(p.longitude).toFixed(5)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
